
import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';

export interface HelpContent {
 what_it_means?: string;
 why_we_ask?: string;
 how_precise?: string;
 examples?: string[];
}

interface Props {
 content: HelpContent;
 label?: string; // For aria-label e.g."Help for Gifts"
}

export const HelpPopover: React.FC<Props> = ({ content, label ="More information" }) => {
 const [isOpen, setIsOpen] = useState(false);
 const containerRef = useRef<HTMLDivElement>(null);

 // Close on click outside
 useEffect(() => {
 const handleClickOutside = (event: MouseEvent) => {
 if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
 setIsOpen(false);
 }
 };

 if (isOpen) {
 document.addEventListener('mousedown', handleClickOutside);
 }
 return () => {
 document.removeEventListener('mousedown', handleClickOutside);
 };
 }, [isOpen]);

 // Close on Escape
 useEffect(() => {
 const handleEsc = (event: KeyboardEvent) => {
 if (event.key === 'Escape') setIsOpen(false);
 };
 if (isOpen) document.addEventListener('keydown', handleEsc);
 return () => document.removeEventListener('keydown', handleEsc);
 }, [isOpen]);

 return (
 <div className="relative inline-flex items-center ml-2 align-middle" ref={containerRef}>
 <button
 type="button"
 onClick={() => setIsOpen(!isOpen)}
 aria-label={label}
 aria-expanded={isOpen}
 className={`
 p-0.5 rounded-full transition-colors
 ${isOpen ? 'text-[#3730A3] bg-[#EEF0FA]' : 'text-[#AFA99F] hover:text-[#3730A3] hover:bg-[#FAFAF8]'}
 `}
 >
 <HelpCircle className="w-4 h-4" />
 </button>

 {isOpen && (
 <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 md:w-80 bg-white rounded border border-[#E8E4DB] z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
 <div className="bg-[#FAFAF8] px-4 py-2 border-b border-[#E8E4DB] flex justify-between items-center">
 <span className="text-xs font-bold text-[#6B6762] uppercase tracking-wider">Help & Context</span>
 <button
 onClick={() => setIsOpen(false)}
 className="text-[#AFA99F] hover:text-[#3F3D39]"
 >
 <X className="w-3.5 h-3.5" />
 </button>
 </div>

 <div className="p-4 space-y-4 text-left">
 {content.what_it_means && (
 <div>
 <h4 className="font-bold text-[#1A1A1A] text-sm mb-1">What this means</h4>
 <p className="text-xs text-[#3F3D39] leading-relaxed">{content.what_it_means}</p>
 </div>
 )}

 {content.why_we_ask && (
 <div>
 <h4 className="font-bold text-[#1A1A1A] text-sm mb-1">Why we ask</h4>
 <p className="text-xs text-[#3F3D39] leading-relaxed">{content.why_we_ask}</p>
 </div>
 )}

 {content.how_precise && (
 <div className="bg-[#EEF0FA] p-2.5 rounded-lg border border-[#C7CBEA]">
 <h4 className="font-bold text-[#3730A3] text-xs mb-1">Precision</h4>
 <p className="text-xs text-[#3730A3] leading-relaxed italic">{content.how_precise}</p>
 </div>
 )}

 {content.examples && content.examples.length > 0 && (
 <div>
 <h4 className="font-bold text-[#1A1A1A] text-sm mb-2">Examples</h4>
 <ul className="list-disc list-outside ml-4 space-y-1">
 {content.examples.map((ex, i) => (
 <li key={i} className="text-xs text-[#3F3D39] leading-relaxed pl-1">{ex}</li>
 ))}
 </ul>
 </div>
 )}
 </div>

 {/* Arrow */}
 <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white border-b border-r border-[#E8E4DB] rotate-45"></div>
 </div>
 )}
 </div>
 );
};
